/**
 * Settings Page
 * Analytics platform settings — persisted via analytics_service backend
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Save } from 'lucide-react';
import Spinner from '../components/shared/Spinner';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import { getSettings, updateSettings } from '../services/analyticsService';

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const REFRESH_OPTIONS = [
  { value: '30', label: '30 seconds' },
  { value: '60', label: '1 minute' },
  { value: '300', label: '5 minutes' },
  { value: '0', label: 'Manual' },
];

const DEFAULT_SETTINGS = {
  timezone: 'UTC',
  default_date_range: '7',
  refresh_interval: '60',
};

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const result = await getSettings();
    if (result.success) {
      setSettings({
        timezone: result.data.timezone || DEFAULT_SETTINGS.timezone,
        default_date_range: result.data.default_date_range || DEFAULT_SETTINGS.default_date_range,
        refresh_interval: result.data.refresh_interval || DEFAULT_SETTINGS.refresh_interval,
      });
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updateSettings(settings);
    if (result.success) {
      toast.success('Settings saved');
    } else {
      toast.error(result.error);
    }
    setSaving(false);
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="max-w-2xl">
        {/* General Settings */}
        <div className="card p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">General Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard Timezone</label>
              <select
                className="input text-sm"
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Date Range</label>
              <select
                className="input text-sm"
                value={settings.default_date_range}
                onChange={(e) => handleChange('default_date_range', e.target.value)}
              >
                {DATE_RANGE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Refresh Interval</label>
              <select
                className="input text-sm"
                value={settings.refresh_interval}
                onChange={(e) => handleChange('refresh_interval', e.target.value)}
              >
                {REFRESH_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Save — the page itself is readable with analytics:read (a manager may
            view settings), so only the write is gated. */}
        <PermissionGate permission={StandardPermission.Analytics.SETTINGS}>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            <Save size={16} />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </PermissionGate>
      </div>
    </div>
  );
};

export default SettingsPage;
